<script lang="ts">
import { cnMerge } from '@/utils/utils.js';

import { buttonSizeClasses } from '../KaButton/button-variants';
import type {
  KaRadioButtonOptionProps,
  KaRadioButtonOptionPropsInternal,
  KaRadioButtonOptionValueType,
} from './types';

export type { KaRadioButtonOptionProps, KaRadioButtonOptionValueType } from './types';
</script>

<script
  setup
  lang="ts"
  generic="T extends KaRadioButtonOptionValueType, ID extends string | number | undefined"
>
import { RadioGroupItem } from 'reka-ui';
import { computed } from 'vue';

import type { NoInfer } from '@/kasten-components/types';

defineOptions({
  name: 'KaRadioButtonGroupOption',
  inheritAttrs: false,
});

type IDValidator = {
  value: undefined extends NoInfer<ID>
    ? NoInfer<T> extends object
      ? '`id` is required for object or array values'
      : NoInfer<T>
    : NoInfer<T>;
};

type AnyOtherAttr = {
  [key: string]: unknown;
};

type Props = KaRadioButtonOptionPropsInternal<T, ID> & IDValidator & AnyOtherAttr;

const props = defineProps<Props>() as KaRadioButtonOptionProps<KaRadioButtonOptionValueType>;

const emit = defineEmits<{
  (e: 'blur', event: FocusEvent): void;
}>();

const sizeClass = computed(() =>
  !props.size || props.size === 'base' ? buttonSizeClasses.base : buttonSizeClasses.small,
);
</script>

<template>
  <RadioGroupItem
    :id="props.id?.toString()"
    :key="props.value?.toString()"
    v-slot="{ checked }"
    as-child
    :value="props.value as any"
    :disabled="props.disabled"
    :aria-label="props.label"
  >
    <button
      type="button"
      :data-testid="$attrs['data-testid']"
      :class="
        cnMerge(
          'flex flex-[2_1_auto] items-center justify-center overflow-hidden whitespace-nowrap rounded font-normal transition-colors',
          props.disabled
            ? 'cursor-not-allowed opacity-50'
            : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700',
          checked
            ? 'bg-blue-500 font-semibold text-white ring-blue-400 hover:bg-blue-600 dark:hover:bg-blue-600'
            : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 dark:bg-navy-800 dark:text-gray-200 dark:ring-white/5',
          sizeClass,
          $attrs.class as string,
        )
      "
      @blur="emit('blur', $event)"
    >
      <span class="flex items-center">
        <component
          :is="props.left.icon"
          v-if="props.left"
          :class="cnMerge('-ml-0.5 mr-2.5 h-4 flex-shrink-0 text-current', props.left.classes)"
          aria-hidden="true"
        />

        <span class="whitespace-nowrap">
          {{ props.label }}
        </span>

        <component
          :is="props.right.icon"
          v-if="props.right"
          :class="cnMerge('-mr-0.5 ml-2.5 h-4 flex-shrink-0 text-current', props.right.classes)"
          aria-hidden="true"
        />
      </span>
    </button>
  </RadioGroupItem>
</template>
